import { BALANCE, BUILDING_DEFS, SPAWN_EDGE } from '../constants';
import type { Simulation } from '../sim';
import type { Peep, SimState } from '../types';
import { releaseTaskClaims } from './claims';
import { pushLedger } from './economy';

export function functionalCapacity(state: SimState, key: 'beds' | 'foodCap' | 'drinkCap' | 'gambleCap'): number {
  let total = 0;
  for (const building of state.buildings) {
    if (!building.functional) continue;
    const value = BUILDING_DEFS[building.type][key];
    if (value) total += Math.floor(value * (building.powerEff > 0 && building.powerEff < 1 ? building.powerEff : 1));
  }
  return total;
}

/** Total people a civic service can realistically serve right now. */
export function serviceCapacity(state: SimState, kind: 'education' | 'health' | 'community'): number {
  let total = 0;
  for (const building of state.buildings) {
    if (!building.functional) continue;
    const def = BUILDING_DEFS[building.type];
    if (def.service !== kind || !def.serviceCap) continue;
    total += Math.floor(def.serviceCap * (building.powerEff > 0 && building.powerEff < 1 ? building.powerEff : 1));
  }
  return total;
}

/** Daily: unlocks, cohort need satisfaction, morale smoothing. */
export function stepNeedsDaily(sim: Simulation): void {
  const state = sim.state;
  const workforce = state.workforce;
  const workers = state.peeps.filter((peep) => peep.role === 'worker');
  if (workers.length === 0) return;
  const bedSleepers = workers.filter((peep) => peep.householdId === null).length;
  const adults = state.peeps.filter((peep) => peep.role !== 'child').length;

  if (!workforce.unlocked.drink && adults >= BALANCE.drinkUnlockPop) workforce.unlocked.drink = true;
  if (!workforce.unlocked.gambling && adults >= BALANCE.gamblingUnlockPop) workforce.unlocked.gambling = true;

  const beds = functionalCapacity(state, 'beds');
  const foodCap = functionalCapacity(state, 'foodCap');
  const drinkCap = functionalCapacity(state, 'drinkCap');
  const gambleCap = functionalCapacity(state, 'gambleCap');

  workforce.needs.housing = bedSleepers === 0 ? 1 : Math.min(1, beds / bedSleepers);
  workforce.needs.food = Math.min(1, foodCap / adults);
  workforce.needs.water = state.water.supplyRatio;
  workforce.needs.drink = workforce.unlocked.drink ? Math.min(1, drinkCap / adults) : 1;
  workforce.needs.gambling = workforce.unlocked.gambling ? Math.min(1, gambleCap / adults) : 1;

  const weights = BALANCE.needWeights;
  let weightSum = weights.housing + weights.food + weights.water + weights.paidOnTime;
  let target =
    weights.housing * workforce.needs.housing +
    weights.food * workforce.needs.food +
    weights.water * workforce.needs.water +
    weights.paidOnTime * workforce.paidOnTime;
  if (workforce.unlocked.drink) {
    weightSum += weights.drink;
    target += weights.drink * workforce.needs.drink;
  }
  if (workforce.unlocked.gambling) {
    weightSum += weights.gambling;
    target += weights.gambling * workforce.needs.gambling;
  }
  target /= weightSum;

  // Families fold their own needs into town morale as they become a real
  // share of the population.
  if (state.households.length > 0) {
    const family = workforce.familyNeeds;
    const familyTarget =
      0.3 * family.education + 0.25 * family.health + 0.2 * family.community + 0.25 * family.air;
    const weight = BALANCE.familyMoraleWeight * Math.min(1, state.households.length / 5);
    target = target * (1 - weight) + familyTarget * weight;
  }
  workforce.morale += BALANCE.moraleSmoothing * (target - workforce.morale);
}

function releasePeepClaims(state: SimState, peep: Peep): void {
  releaseTaskClaims(state, peep);
  for (const building of state.buildings) {
    const index = building.staffAssigned.indexOf(peep.id);
    if (index >= 0) building.staffAssigned.splice(index, 1);
  }
}

/** Weekly: quit rolls driven by morale + missed payroll, damped by tier
 * loyalty. A settler who quits takes the whole household with them. */
export function stepQuitsWeekly(sim: Simulation): void {
  const state = sim.state;
  const workforce = state.workforce;
  const rng = sim.forks.workforce;
  const base =
    Math.max(0, Math.min(0.5, BALANCE.quitBase - workforce.morale)) * BALANCE.quitScale +
    BALANCE.quitMissedPayrollFactor * Math.min(workforce.payrollMissedStreak, 2);
  const workers = state.peeps.filter((peep) => peep.role === 'worker');
  const quitters: Peep[] = [];
  for (const peep of workers) {
    // Unwaged (idle-job) workers drift away half again as fast.
    const idleFactor = peep.job === 'idle' ? 1.5 : 1;
    const probability = base * (BALANCE.tierQuitFactor[peep.tier] ?? 1) * idleFactor;
    if (rng.chance(probability)) quitters.push(peep);
  }
  // Never let the site fully empty from one bad week — someone always stays.
  if (quitters.length >= workers.length && workers.length > 0) quitters.pop();

  let departed = 0;
  let householdsLost = 0;
  for (const quitter of quitters) {
    const leaving: Peep[] = [quitter];
    if (quitter.householdId !== null) {
      const householdIndex = state.households.findIndex((candidate) => candidate.id === quitter.householdId);
      if (householdIndex >= 0) {
        const household = state.households[householdIndex]!;
        for (const id of [household.spouseId, ...household.childIds]) {
          const member = state.peeps.find((peep) => peep.id === id);
          if (member) leaving.push(member);
        }
        state.households.splice(householdIndex, 1);
        householdsLost += 1;
      }
    }
    for (const peep of leaving) {
      releasePeepClaims(state, peep);
      const index = state.peeps.indexOf(peep);
      if (index >= 0) state.peeps.splice(index, 1);
      departed += 1;
    }
  }
  workforce.weeklyQuits = quitters.length;
  if (quitters.length > 0) {
    sim.pushEvent({ kind: 'quit', count: departed });
    sim.pushEvent({
      kind: 'toast',
      message:
        householdsLost > 0
          ? `${quitters[0]!.name}'s whole family packed up and left town.`
          : quitters.length === 1
            ? `${quitters[0]!.name} quit and took the bus out.`
            : `${quitters.length} workers quit and took the bus out.`,
    });
  }
}

/**
 * Player-ordered downsizing: let go of unassigned single workers, cheapest
 * (lowest tier) first, at one week's tier wage severance each. Families are
 * townsfolk, not employees — never dismissible. Deterministic: sorted by
 * (tier, id), no RNG, and severance never overdraws the till.
 */
export function dismissWorkers(sim: Simulation, count: number): void {
  const state = sim.state;
  const eligible = state.peeps
    .filter((peep) => peep.role === 'worker' && peep.householdId === null && peep.job === 'idle')
    .sort((a, b) => a.tier - b.tier || a.id - b.id);
  if (eligible.length === 0) {
    sim.pushEvent({ kind: 'toast', message: 'No idle single workers to let go.' });
    return;
  }
  let dismissed = 0;
  let severanceTotal = 0;
  let firstName = '';
  for (const peep of eligible) {
    if (dismissed >= Math.floor(count)) break;
    const severance = BALANCE.tierWageCents[peep.tier] ?? BALANCE.tierWageCents[0];
    if (state.economy.cashCents - severanceTotal < severance) break;
    severanceTotal += severance;
    if (dismissed === 0) firstName = peep.name;
    releasePeepClaims(state, peep);
    const index = state.peeps.indexOf(peep);
    if (index >= 0) state.peeps.splice(index, 1);
    dismissed += 1;
  }
  if (dismissed === 0) {
    sim.pushEvent({ kind: 'toast', message: 'Not enough cash in the till for severance.' });
    return;
  }
  state.economy.cashCents -= severanceTotal;
  state.economy.weekSeveranceCents += severanceTotal;
  pushLedger(state, sim.day, 'severance', -severanceTotal, `Severance (${dismissed} let go)`);
  sim.pushEvent({ kind: 'dismissed', count: dismissed, severanceCents: severanceTotal });
  sim.pushEvent({
    kind: 'toast',
    message:
      dismissed === 1
        ? `${firstName} took the severance and caught the bus out.`
        : `${dismissed} workers took severance and left town.`,
  });
}

/** Word spreads with empty bunks: a boomtown building housing draws bigger buses. */
export function weeklyApplicants(morale: number, saloonOpen: boolean, vacantBeds: number): number {
  return Math.min(
    BALANCE.applicantsMax,
    2 + Math.floor(morale * 4) + (saloonOpen ? 1 : 0) + Math.floor(vacantBeds / 8),
  );
}

/** Weekly: the Monday bus brings applicants if hiring is open and beds exist. */
export function stepHiringWeekly(sim: Simulation): void {
  const state = sim.state;
  const workforce = state.workforce;
  workforce.weeklyHires = 0;
  if (!workforce.hiringOpen || state.meta.gameOver) return;
  const saloonOpen = state.buildings.some((building) => building.type === 'saloon' && building.functional);
  const beds = functionalCapacity(state, 'beds');
  const bedSleepers = state.peeps.filter((peep) => peep.role === 'worker' && peep.householdId === null).length;
  const vacant = Math.max(0, beds - bedSleepers);
  const applicants = weeklyApplicants(workforce.morale, saloonOpen, vacant);
  const hires = Math.min(applicants, vacant);
  for (let index = 0; index < hires; index += 1) {
    sim.spawnPeep(SPAWN_EDGE.x + 0.5, SPAWN_EDGE.y + 0.5 + sim.forks.hiring.range(-0.3, 0.3), 'idle');
  }
  workforce.weeklyHires = hires;
  if (hires > 0) {
    sim.pushEvent({ kind: 'hire', count: hires });
    sim.pushEvent({ kind: 'toast', message: `The bus brought ${hires} new worker${hires === 1 ? '' : 's'}.` });
  }
}
